import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseDownstreamFlowsFilteredByTableQuery,
  DatabaseDownstreamFlowsFilteredByTableQueryVariables
} from '../../../gen-src/queries/DatabaseDownstreamFlowsFilteredByTableQuery';
import {
  DatabaseDownstreamFlowsFilteredByTableWithoutPersonalSpaceQuery,
  DatabaseDownstreamFlowsFilteredByTableWithoutPersonalSpaceQueryVariables
} from '../../../gen-src/queries/DatabaseDownstreamFlowsFilteredByTableWithoutPersonalSpaceQuery';
import {
  DatabaseDownstreamFlowsQuery,
  DatabaseDownstreamFlowsQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamFlowsQuery';
import {
  DatabaseDownstreamFlowsWithoutPersonalSpaceQuery,
  DatabaseDownstreamFlowsWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamFlowsWithoutPersonalSpaceQuery';
import { FlowsDisplayGridRowDataFragment } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragment';
import { FlowsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragmentWithoutPersonalSpace';
import { FlowSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatabaseDownstreamFlowsFilteredByTableQueryGql } from '../../queries/DatabaseDownstreamFlowsFilteredByTableQueryGql';
import { DatabaseDownstreamFlowsFilteredByTableWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseDownstreamFlowsFilteredByTableWithoutPersonalSpaceQueryGql';
import { DatabaseDownstreamFlowsQueryGql } from '../../queries/DatabaseDownstreamFlowsQueryGql';
import { DatabaseDownstreamFlowsWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseDownstreamFlowsWithoutPersonalSpaceQueryGql';
import {
  FlowsDisplayGrid,
  FlowsDisplayGridRowData,
  getRowDataForFlow,
  getRowDataForFlowWithoutPersonalSpace,
} from '../sharedDisplayGrid/FlowsDisplayGrid';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

type QueryVariables = DatabaseDownstreamFlowsQueryVariables;
type QueryResults = DatabaseDownstreamFlowsQuery;
type QueryResultListNode = FlowsDisplayGridRowDataFragment;
type GridRowData = FlowsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = DatabaseDownstreamFlowsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = DatabaseDownstreamFlowsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = FlowsDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = DatabaseDownstreamFlowsFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseDownstreamFlowsFilteredByTableQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = DatabaseDownstreamFlowsFilteredByTableWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = DatabaseDownstreamFlowsFilteredByTableWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamFlowsConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamFlowsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamFlowsConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamFlowsConnection;
}

export type DatabaseDownstreamFlowsQueryGridProps = GridSortProps<FlowsValidSortFields> & {
  readonly databaseId: string;
  readonly filteredTableId: string | null;
  readonly rowCount: number | null;
};

export function DatabaseDownstreamFlowsQueryGrid(props: DatabaseDownstreamFlowsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamFlowsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredFlowsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={DatabaseDownstreamFlowsFilteredByTableQueryGql}
        partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) =>
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatabaseDownstreamFlowsQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) =>
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseDownstreamFlowsFilteredByTableWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseDownstreamFlowsWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  );
}
